import { useState } from 'react';
import { operatorConsole, type SecurityState } from '../../lib/api/operatorConsole';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { FormField } from '../../components/ui/FormField';

/**
 * The team policy, deliberately separate from enrolling your own
 * authenticator. Turning this on makes every teammate set up 2FA at their
 * next sign-in — it used to happen as an invisible side effect of the owner
 * enrolling, which is not something anyone asked for.
 *
 * There is no off switch here on purpose: undoing a security requirement is a
 * support action, so a stolen owner session cannot quietly lift it.
 */
export function TeamTwoFactorCard({
  required,
  selfEnrolled,
  onChanged,
}: {
  required: boolean;
  /** Whether the owner has an authenticator of their own. The requirement
   *  applies to them too, so the backend refuses until they do — asking here
   *  saves them a confusing error. */
  selfEnrolled: boolean;
  onChanged: (state: SecurityState) => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function enable() {
    setError(null);
    setPending(true);
    try {
      const next = await operatorConsole.requireTeam2fa(password);
      setPassword('');
      onChanged(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not turn this on');
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-6 flex flex-col gap-4">
      <header>
        <h2 className="text-lg font-semibold">Require 2FA from the whole team</h2>
        <p className="text-sm text-[var(--co-muted)]">
          Everyone who signs into this console has to set up an authenticator app. Each person
          gets their own — yours does not let anyone else in.
        </p>
      </header>

      {required ? (
        <p className="text-sm text-[var(--co-success)]" role="status">
          Required for everyone on this console. To lift it, contact support.
        </p>
      ) : !selfEnrolled ? (
        <p className="text-sm text-[var(--co-muted)]">
          Set up your own authenticator above first — this requirement applies to you too, and
          turning it on without one would sign you out immediately.
        </p>
      ) : (
        <>
          <FormField label="Current password" htmlFor="sec-team-2fa-pw" error={error ?? undefined}>
            <Input
              id="sec-team-2fa-pw"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </FormField>
          <div>
            <Button
              type="button"
              onClick={() => void enable()}
              disabled={pending || password === ''}
            >
              {pending ? 'Turning on…' : 'Require for everyone'}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
